/**
 * Demo salon "סמדר ציפורניים" (slug `smadar`). Deterministic for a given (seed, now): the same inputs
 * always produce byte-identical data, and everything is placed relative to `now` (3 weeks back,
 * 3 weeks ahead) so the demo always looks live.
 */
import { renderTemplate } from '../adapters/messaging/render';
import type { TemplateCall } from '../adapters/messaging/templates';
import type { Tables } from '../adapters/types';
import { createLinks } from '../config/env';
import { shekelsToAgorot } from '../domain/money';
import { nextSendTime, previousSendTime } from '../domain/reminders';
import { daypartOf } from '../domain/waitlist';
import { DAY, HOUR, MINUTE, addDaysToKey, atMinuteOfDay, dayKeyOf, formatDate, formatTime, parseHHMM, toIso, weekdayOfDay } from '../domain/time';
import {
  BUSINESS_TZ, CONSENT_WORDING_VERSION, DEFAULT_SETTINGS, ulid, type Appointment, type AppointmentStatus, type Business, type BrandPhoto, type Client, type ConsentRecord,
  type Id, type Message, type Owner, type Payment, type Service, type Settings, type Staff, type TimeOff, type WaitlistEntry, type WeeklyHours,
} from '../domain/types';
import { emptyTables } from '../adapters/storage/base';
import { PROFESSION_PRESETS } from './professionPresets';

export const DEMO_SEED = 20261031;
export const DEMO_SLUG = 'smadar';

export interface SeedOptions {
  /** Epoch ms the data is built around (the demo clock's now). */
  now: number;
  /** PRNG seed; default DEMO_SEED. */
  seed?: number;
  /** Base for manage/offer links inside seeded messages. */
  baseUrl?: string;
}
export interface SeedResult {
  /** Pass to StorageAdapter.reset(). */
  data: Tables;
  businessId: Id;
  ownerId: Id;
  staffIds: Id[];
  serviceIds: Id[];
  /** Appointment that was just cancelled and has 3 waiting clients (drives the Today offer card). */
  openedSlotAppointmentId: Id;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST = ['נועה', 'מאיה', 'שירה', 'יעל', 'תמר', 'נטע', 'הדר', 'ליאור', 'רוני', 'דנה', 'מיכל', 'אורית', 'שני', 'עדי', 'גלית', 'רונית', 'אביגיל', 'ספיר', 'לילך', 'קרן', 'אילנה', 'מורן', 'סיוון', 'עינת', 'טל', 'ענת', 'רחל', 'שרון', 'לימור', 'ורד', 'אסנת', 'חן', 'מעיין', 'אלה', 'הילה', 'נועם', 'בר', 'דורית', 'ג׳ולי', 'סהר', 'אורלי', 'מירב', 'יובל', 'עומר'];
const LAST = ['כהן', 'לוי', 'מזרחי', 'פרץ', 'ביטון', 'אברהם', 'דהן', 'אלון', 'שלום', 'חזן', 'אזולאי', 'גבאי', 'בן דוד', 'אוחיון', 'מלכה', 'עמר', 'סעדון', 'רבינוביץ׳', 'גולדשטיין', 'פרידמן', 'שפירא', 'קליין', 'חדד', 'וקנין'];
const TAGS = ['VIP', 'כלה', 'לקוחה חדשה', 'מגיעה עם חברה'];

function pick<T>(rng: () => number, xs: readonly T[]): T {
  return xs[Math.floor(rng() * xs.length)] as T;
}

export function seed(opts: SeedOptions): SeedResult {
  const rng = mulberry32(opts.seed ?? DEMO_SEED);
  const now = opts.now;
  const iso = toIso(now);
  const links = createLinks(opts.baseUrl ?? 'https://planella.co.il/');
  const mk = (atMs: number = now): Id => ulid(atMs, rng);
  const tz = BUSINESS_TZ;
  const today = dayKeyOf(now, tz);
  const data = emptyTables();

  // ---------------------------------------------------------------- business, staff, services
  const businessId = mk(now - 45 * DAY);
  const created = toIso(now - 45 * DAY);
  const business: Business = {
    id: businessId, businessId, createdAt: created, updatedAt: iso, slug: DEMO_SLUG, name: 'סמדר ציפורניים', profession: 'nails', tz, locale: 'he',
    brand: { color: 'violet', photos: demoPhotos() }, plan: 'trial', trialStartedAt: toIso(now - 9 * DAY), topUpMessages: 0, isDemo: true,
    about: 'סטודיו ביתי לציפורניים בכפר סבא. לק ג׳ל, בניה ומילוי, בניחוח של קפה טוב.', phone: '+972521234567', whatsapp: '+972521234567',
    address: 'הרימון 8, כפר סבא', instagram: 'smadar.nails',
  };
  data.businesses.push(business);
  const owner: Owner = { id: mk(now - 45 * DAY), businessId, createdAt: created, updatedAt: created, phone: '+972521234567', name: 'סמדר' };
  data.owners.push(owner);
  const settings: Settings = {
    ...DEFAULT_SETTINGS, id: businessId, businessId, createdAt: created, updatedAt: created,
    lateCancelFee: { kind: 'percent', percent: 50 }, noShowFee: { kind: 'fixed', amountAgorot: 5000 },
    // Demo: cash at the venue, card through the mock gateway, Bit and PayBox as owner-provided links.
    paymentMethods: {
      cash: true, card: true,
      bit: { enabled: true, link: 'https://example.com/bit/smadar-demo', phone: '052-1234567' },
      paybox: { enabled: true, link: 'https://example.com/paybox/smadar-demo' },
    },
  };
  data.settings.push(settings);

  const staff: Staff[] = [
    { id: mk(now - 45 * DAY), businessId, createdAt: created, updatedAt: created, name: 'סמדר', active: true, colorKey: 1, sortOrder: 0, phone: owner.phone },
    { id: mk(now - 40 * DAY), businessId, createdAt: created, updatedAt: created, name: 'נועה', active: true, colorKey: 3, sortOrder: 1 },
  ];
  data.staff.push(...staff);
  const [smadar, noa] = staff as [Staff, Staff];

  const services: Service[] = PROFESSION_PRESETS.nails.services.map((s, i) => ({
    id: mk(now - 45 * DAY), businessId, createdAt: created, updatedAt: created, name: s.name, category: s.category, durationMin: s.durationMin,
    bufferMin: s.bufferMin, priceAgorot: shekelsToAgorot(s.priceNis), ...(s.depositNis ? { depositAgorot: shekelsToAgorot(s.depositNis) } : {}),
    // Noa does polish, basic manicure and removal; Smadar does everything.
    staffIds: i === 0 || i === 1 || i === 5 ? [smadar.id, noa.id] : [smadar.id], active: true, sortOrder: i,
  }));
  data.services.push(...services);

  const hours = (st: Staff, ranges: Record<number, Array<[string, string]>>): WeeklyHours[] => [0, 1, 2, 3, 4, 5, 6].map((weekday) => ({
    id: mk(now - 45 * DAY), businessId, createdAt: created, updatedAt: created, staffId: st.id, weekday,
    ranges: (ranges[weekday] ?? []).map(([start, end]) => ({ start, end })),
  }));
  const lunch: Array<[string, string]> = [['09:00', '13:00'], ['13:30', '18:00']];
  data.weeklyHours.push(
    ...hours(smadar, { 0: lunch, 1: lunch, 2: lunch, 3: lunch, 4: lunch, 5: [['09:00', '13:00']] }),
    ...hours(noa, { 0: [['10:00', '17:00']], 2: [['10:00', '17:00']], 3: [['10:00', '17:00']] }),
  );
  const offStart = atMinuteOfDay(addDaysToKey(today, 16), 0, tz);
  const timeOff: TimeOff = {
    id: mk(), businessId, createdAt: iso, updatedAt: iso, staffId: noa.id, start: toIso(offStart), end: toIso(offStart + 3 * DAY), reason: 'חופשה',
  };
  data.timeOff.push(timeOff);

  // ---------------------------------------------------------------- clients, consent
  const clients: Client[] = [];
  const usedPhones = new Set<string>();
  for (let i = 0; i < 44; i++) {
    let phone = '';
    do { phone = `+9725${pick(rng, [0, 2, 3, 4, 8])}${String(Math.floor(rng() * 1e7)).padStart(7, '0')}`; } while (usedPhones.has(phone));
    usedPhones.add(phone);
    const newcomer = i >= 30;
    const joined = toIso(newcomer ? now - Math.floor(rng() * 20 + 1) * DAY : now - (30 + Math.floor(rng() * 200)) * DAY);
    clients.push({
      id: mk(Date.parse(joined)), businessId, createdAt: joined, updatedAt: joined, name: `${pick(rng, FIRST)} ${pick(rng, LAST)}`, phone,
      workNotes: i % 6 === 0 ? 'ג׳ל בגוון ורוד פודרה, אורך בינוני' : i % 9 === 0 ? 'אוהבת צורת שקד' : '',
      sensitiveNotes: i % 11 === 0 ? 'אלרגיה לאקריליק' : '', tags: i % 10 === 0 ? ['VIP'] : newcomer ? [TAGS[2] as string] : [],
    });
  }
  data.clients.push(...clients);

  const noConsent = new Set([3, 8, 15, 22, 27, 34, 41].map((i) => clients[i]?.id));
  const revoked = new Set([5, 19, 37].map((i) => clients[i]?.id));
  const hasReminders = (c: Client): boolean => !noConsent.has(c.id) && !revoked.has(c.id);
  for (const [i, c] of clients.entries()) {
    const grantedAt = toIso(Date.parse(c.createdAt) + 2 * MINUTE);
    const base = { businessId, clientId: c.id, channel: 'whatsapp' as const, wordingVersion: '2026-10-v1', source: 'booking_page' as const };
    if (!noConsent.has(c.id)) {
      const row: ConsentRecord = { ...base, id: mk(), createdAt: grantedAt, updatedAt: grantedAt, purpose: 'reminders', grantedAt, revokedAt: revoked.has(c.id) ? toIso(now - 5 * DAY) : null };
      data.consents.push(row);
    }
    if (i % 3 === 0 && !noConsent.has(c.id)) {
      data.consents.push({ ...base, id: mk(), createdAt: grantedAt, updatedAt: grantedAt, purpose: 'marketing', grantedAt, revokedAt: null });
    }
  }

  // ---------------------------------------------------------------- appointments (21 days back, 21 ahead)
  const appointments: Appointment[] = [];
  const payments: Payment[] = [];
  const hoursOf = new Map(data.weeklyHours.map((w) => [`${w.staffId}:${w.weekday}`, w.ranges]));
  const offDays = new Set<string>();
  for (let t = offStart; t < offStart + 3 * DAY; t += DAY) offDays.add(`${noa.id}:${dayKeyOf(t, tz)}`);

  for (let delta = -21; delta <= 21; delta++) {
    const day = addDaysToKey(today, delta);
    const fill = delta < 0 ? 0.8 : delta === 0 ? 0.65 : Math.max(0.3, 0.72 - delta * 0.015);
    for (const st of staff) {
      if (offDays.has(`${st.id}:${day}`)) continue;
      for (const r of hoursOf.get(`${st.id}:${weekdayOfDay(day)}`) ?? []) {
        let cursor = parseHHMM(r.start);
        const end = parseHHMM(r.end);
        while (cursor < end) {
          if (rng() > fill) { cursor += 15 * Math.ceil((15 + rng() * 45) / 15); continue; }
          const options = services.filter((s) => s.staffIds.includes(st.id));
          const service = rng() < 0.4 ? (options[0] as Service) : pick(rng, options);
          if (cursor + service.durationMin > end) break;
          const startMs = atMinuteOfDay(day, cursor, tz);
          // Today starts with what is still to come: the first Today row is always actionable (move / cancel / done). History lives on earlier days.
          if (delta === 0 && startMs < now + 90 * MINUTE) { cursor += 15 * Math.ceil((service.durationMin + service.bufferMin) / 15); continue; }
          const pool = clients.filter((c) => Date.parse(c.createdAt) < startMs - HOUR);
          const client = rng() < 0.7 ? pool[Math.floor(Math.pow(rng(), 2) * Math.min(30, pool.length))] as Client : pick(rng, pool);
          appointments.push(buildAppointment(service, st, client, startMs));
          cursor += Math.ceil((service.durationMin + service.bufferMin) / 15) * 15;
        }
      }
    }
  }

  function buildAppointment(service: Service, st: Staff, client: Client, startMs: number): Appointment {
    const past = startMs + service.durationMin * MINUTE <= now;
    const roll = rng();
    const status: AppointmentStatus = past
      ? roll < 0.84 ? 'completed' : roll < 0.9 ? 'no_show' : roll < 0.97 ? 'cancelled_client' : 'cancelled_owner'
      : roll < 0.8 ? 'confirmed' : roll < 0.88 ? 'pending' : 'cancelled_client';
    const bookedAt = Math.min(startMs - (6 + Math.floor(rng() * 150)) * HOUR, now - MINUTE);
    const id = mk(bookedAt);
    const cancelled = status === 'cancelled_client' || status === 'cancelled_owner';
    const paidDeposit = service.depositAgorot !== undefined && !cancelled;
    const a: Appointment = {
      id, businessId, createdAt: toIso(bookedAt), updatedAt: toIso(bookedAt), clientId: client.id, serviceId: service.id, staffId: st.id,
      start: toIso(startMs), end: toIso(startMs + service.durationMin * MINUTE), blockedUntil: toIso(startMs + (service.durationMin + service.bufferMin) * MINUTE),
      status, source: rng() < 0.7 ? 'link' : 'owner', manageToken: ulid(bookedAt, rng).toLowerCase(), priceAgorot: service.priceAgorot,
      depositAgorot: service.depositAgorot ?? 0, depositStatus: paidDeposit ? 'paid' : service.depositAgorot ? 'refunded' : 'none',
      ...(cancelled ? { cancelledAt: toIso(past ? Math.min(now - HOUR, startMs - (2 + Math.floor(rng() * 70)) * HOUR) : now - (26 + Math.floor(rng() * 90)) * HOUR), cancelReason: status === 'cancelled_owner' ? 'owner' as const : 'client' as const } : {}),
      ...(status === 'completed' ? (() => {
        const paidAt = toIso(startMs + service.durationMin * MINUTE);
        const balance = service.priceAgorot - (paidDeposit ? service.depositAgorot ?? 0 : 0);
        const method = (['cash', 'bit', 'paybox', 'card'] as const)[id.charCodeAt(id.length - 1) % 4] ?? 'cash';
        return { paidAt, paidAgorot: balance, payment: { method, paidAgorot: balance, paidAt } };
      })() : {}),
      ...(paidDeposit && service.depositAgorot ? { depositMethod: 'card' as const } : {}),
    };
    if (paidDeposit) {
      const paymentId = mk(bookedAt);
      payments.push({ id: paymentId, businessId, createdAt: a.createdAt, updatedAt: a.createdAt, appointmentId: id, amountAgorot: a.depositAgorot, refundedAgorot: 0, status: 'paid', gateway: 'mock', expiresAt: toIso(bookedAt + 15 * MINUTE), paidAt: a.createdAt });
      a.paymentId = paymentId;
    }
    return a;
  }

  // The first appointment of today is an upcoming, confirmed one.
  const todayKey = dayKeyOf(now, tz);
  const firstToday = appointments.filter((a) => dayKeyOf(Date.parse(a.start), tz) === todayKey && Date.parse(a.start) > now).sort((x, y) => x.start.localeCompare(y.start))[0];
  if (firstToday && firstToday.status !== 'confirmed') {
    const { cancelledAt: _c, cancelReason: _r, ...rest } = firstToday;
    Object.assign(firstToday, rest, { status: 'confirmed' as const });
    delete firstToday.cancelledAt; delete firstToday.cancelReason;
    if (firstToday.depositAgorot > 0) firstToday.depositStatus = 'paid';
  }

  // Scenario 1: two deposit holds that are still ticking (Today shows "awaiting deposit").
  const holdable = appointments.filter((a) => a.status === 'confirmed' && a.depositAgorot > 0 && Date.parse(a.start) > now + 12 * HOUR);
  for (const a of holdable.slice(0, 2)) {
    const stale = a.paymentId ? payments.findIndex((p) => p.id === a.paymentId) : -1;
    if (stale >= 0) payments.splice(stale, 1);
    const paymentId = mk(now - 6 * MINUTE);
    payments.push({ id: paymentId, businessId, createdAt: toIso(now - 6 * MINUTE), updatedAt: toIso(now - 6 * MINUTE), appointmentId: a.id, amountAgorot: a.depositAgorot, refundedAgorot: 0, status: 'pending', gateway: 'mock', expiresAt: toIso(now + 9 * MINUTE) });
    Object.assign(a, { status: 'awaiting_deposit', depositStatus: 'required', paymentId, holdExpiresAt: toIso(now + 9 * MINUTE), createdAt: toIso(now - 6 * MINUTE) });
    delete a.paidAt;
  }

  // Scenario 2: a fresh cancellation tomorrow or later with 3 matching waiting clients.
  const candidate = appointments.find((a) => {
    const startMs = Date.parse(a.start);
    return a.status === 'confirmed' && startMs > now + 20 * HOUR && startMs < now + 6 * DAY && a.staffId === smadar.id && !a.depositAgorot;
  }) ?? appointments.find((a) => a.status === 'confirmed' && Date.parse(a.start) > now + 20 * HOUR && !a.depositAgorot) as Appointment;
  Object.assign(candidate, { status: 'cancelled_client', cancelledAt: toIso(now - 40 * MINUTE), cancelReason: 'client', updatedAt: toIso(now - 40 * MINUTE) });
  const slotDay = dayKeyOf(Date.parse(candidate.start), tz);
  const waiting = clients.filter((c) => hasReminders(c) && c.id !== candidate.clientId).slice(8, 11);
  const waitlist: WaitlistEntry[] = waiting.map((c, i) => ({
    id: mk(now - (3 - i) * DAY), businessId, createdAt: toIso(now - (3 - i) * DAY), updatedAt: toIso(now - (3 - i) * DAY), clientId: c.id,
    serviceId: candidate.serviceId, days: [slotDay, addDaysToKey(slotDay, 1)], daypart: i === 0 ? 'any' : daypartOf(Date.parse(candidate.start), tz), status: 'waiting',
    ...(i === 2 ? { staffId: candidate.staffId } : {}),
  }));
  data.waitlistEntries.push(...waitlist);

  data.appointments.push(...appointments);
  data.payments.push(...payments);

  // ---------------------------------------------------------------- outbox
  const messages: Message[] = [];
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const nameOf = (id: Id): string => (clientById.get(id)?.name ?? '').split(' ')[0] ?? '';
  const push = (a: Appointment, call: TemplateCall, rawMs: number, status: Message['status'], reason?: Message['suppressionReason']): void => {
    // Seeded history respects the 08:00-20:00 / Shabbat send window (the same rule the live scheduler applies).
    const quiet = { start: '08:00', end: '20:00', blockShabbat: true } as const;
    let atMs: number;
    if (status !== 'queued') atMs = previousSendTime(rawMs, quiet, undefined, tz) ?? rawMs;
    else {
      // Pending reminders: next allowed instant, or the last allowed one before the visit if that would be too late (Fri before 16:00).
      const fwd = nextSendTime(rawMs, quiet, Date.parse(a.start), tz);
      atMs = fwd + HOUR <= Date.parse(a.start) ? fwd : (previousSendTime(rawMs, quiet, Date.parse(a.start), tz) ?? fwd);
    }
    const c = clientById.get(a.clientId) as Client;
    const at = toIso(atMs);
    messages.push({
      id: mk(atMs), businessId, createdAt: at, updatedAt: at, channel: 'whatsapp', template: call.template, to: c.phone, params: { ...call.params }, status,
      ...(reason ? { suppressionReason: reason } : {}), scheduledFor: at, ...(status === 'delivered' ? { sentAt: at, deliveredAt: toIso(atMs + 5000) } : {}),
      appointmentId: a.id, clientId: c.id, purpose: 'reminders', dedupeKey: `${call.template}:${a.id}${call.template === 'reminder_24h' ? `:${a.start}` : ''}`,
      text: renderTemplate(call, 'he'), mock: true,
    });
  };
  const common = (a: Appointment) => {
    const s = services.find((x) => x.id === a.serviceId) as Service;
    const startMs = Date.parse(a.start);
    return { businessName: business.name, clientName: nameOf(a.clientId), serviceName: s.name, date: formatDate(startMs, 'long', 'he-IL', tz), time: formatTime(startMs, 'he-IL', tz) };
  };
  for (const a of appointments) {
    const startMs = Date.parse(a.start);
    if (startMs < now - 3 * DAY || startMs > now + 7 * DAY || a.status === 'awaiting_deposit') continue;
    const c = clientById.get(a.clientId) as Client;
    const manageUrl = links.manage(a.manageToken);
    if (a.status === 'cancelled_client' || a.status === 'cancelled_owner') {
      if (hasReminders(c) && a.cancelledAt && Date.parse(a.cancelledAt) > now - 3 * DAY) {
        const template = a.status === 'cancelled_owner' ? 'cancelled_by_owner' : 'cancelled_by_client_ack';
        push(a, { template, params: { ...common(a), bookUrl: links.booking(DEMO_SLUG) } }, Date.parse(a.cancelledAt), 'delivered');
      }
      continue;
    }
    const confirmedAt = Date.parse(a.createdAt);
    if (!hasReminders(c)) {
      if (confirmedAt > now - 3 * DAY) push(a, { template: 'booking_confirmed', params: { ...common(a), manageUrl } }, confirmedAt, 'suppressed', 'no_consent');
      continue;
    }
    if (confirmedAt > now - 3 * DAY) push(a, { template: 'booking_confirmed', params: { ...common(a), manageUrl } }, confirmedAt, 'delivered');
    const remindAt = startMs - 24 * HOUR;
    if (remindAt > confirmedAt + HOUR) push(a, { template: 'reminder_24h', params: { ...common(a), manageUrl } }, remindAt, remindAt < now - 10_000 ? 'delivered' : 'queued');
  }
  data.messages.push(...messages);

  return { data, businessId, ownerId: owner.id, staffIds: staff.map((s) => s.id), serviceIds: services.map((s) => s.id), openedSlotAppointmentId: candidate.id };
}

/** Generated SVG "work" photos (nail-art swatches) so the public gallery strip shows in the demo without any binary assets. */
const PHOTO_PALETTE: Array<[string, string, string]> = [
  ['#C9A8F0', '#7C4DDB', 'ג׳ל סגול'], ['#F7B2C4', '#E0527A', 'ורוד פרחוני'], ['#9BD7CF', '#2E8F86', 'מנטה'],
  ['#FFD59E', '#F08A24', 'שמש'], ['#BFD3FF', '#4466D6', 'כחול שמיים'], ['#E8DCCB', '#9C7B58', 'נוד חם'],
];
/** Six different compositions (five nails, french tips, floral, swirl, dots, stripes) so the strip never repeats one picture. */
function photoArt(kind: number, a: string, b: string): string {
  const nail = (x: number, y: number, rx: number, ry: number, fill: string, op = 0.85): string => `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${fill}" fill-opacity="${op}"/>`;
  switch (kind) {
    case 0: return [0, 1, 2, 3, 4].map((n) => nail(60 + n * 70, 230 - (n === 2 ? 20 : n % 2 ? 0 : 8), 26, 60 + (n === 2 ? 10 : 0), '#fff', 0.82) + nail(60 + n * 70, 196 - (n === 2 ? 20 : n % 2 ? 0 : 8), 14, 22, b, 0.35)).join('');
    case 1: return [0, 1, 2, 3].map((n) => nail(80 + n * 80, 250, 30, 78, '#FBE6D8', 0.95) + `<path d="M${50 + n * 80} 196 Q${80 + n * 80} 160 ${110 + n * 80} 196 L${110 + n * 80} 210 L${50 + n * 80} 210Z" fill="#fff"/>`).join('');
    case 2: return [0, 1, 2, 3, 4, 5].map((n) => { const cx = 90 + (n % 3) * 110; const cy = 120 + Math.floor(n / 3) * 150; return [0, 1, 2, 3, 4].map((k) => nail(cx + Math.cos(k * 1.2566) * 24, cy + Math.sin(k * 1.2566) * 24, 16, 16, '#fff', 0.9)).join('') + nail(cx, cy, 10, 10, b, 0.8); }).join('');
    case 3: return `<path d="M40 300 C120 80 200 360 360 100" stroke="#fff" stroke-width="26" fill="none" stroke-opacity=".85" stroke-linecap="round"/><path d="M40 360 C140 180 220 380 370 200" stroke="${b}" stroke-width="16" fill="none" stroke-opacity=".5" stroke-linecap="round"/>`;
    case 4: return Array.from({ length: 28 }, (_, n) => nail(40 + (n % 7) * 55, 60 + Math.floor(n / 7) * 85, 14 + (n % 3) * 4, 14 + (n % 3) * 4, n % 2 ? '#fff' : b, n % 2 ? 0.8 : 0.45)).join('');
    default: return Array.from({ length: 7 }, (_, n) => `<rect x="${-20 + n * 62}" y="-20" width="26" height="460" fill="${n % 2 ? '#fff' : b}" fill-opacity="${n % 2 ? 0.7 : 0.35}" transform="rotate(18 200 200)"/>`).join('');
  }
}
function demoPhotos(): BrandPhoto[] {
  return PHOTO_PALETTE.map(([a, b, alt], i) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><defs><linearGradient id="g" x1="${i % 2}" y1="0" x2="${1 - (i % 2)}" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${b}"/></linearGradient></defs><rect width="400" height="400" fill="url(#g)"/>${photoArt(i, a, b)}</svg>`;
    return { id: `demo-photo-${i + 1}`, dataUrl: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`, alt, order: i };
  });
}

/** Seed with no data at all (the empty-state demo). */
export function emptySeed(): Tables { return emptyTables(); }
